// src/strategy/CaptureMoveDetector.ts
import { Position, BoardState } from '../model/BoardState';
import logger from '../utils/Logger';

/**
 * Utility class to detect and evaluate capture moves across different strategies.
 */
export class CaptureMoveDetector {
  /**
   * Checks if a move is a capture move.
   */
  public static isCaptureMove(move: Position, boardState: BoardState, playerColor: string): boolean {
    // More explicit logging to debug capture detection
    const capturedPiece = boardState.pieces.find(p => 
      p.position.row === move.row && 
      p.position.col === move.col && 
      p.color !== playerColor
    );
    
    if (capturedPiece) {
      logger.debug(`Capture detected: ${playerColor} can capture ${capturedPiece.color} ${capturedPiece.type} at (${move.row}, ${move.col})`);
      return true;
    }
    
    return false;
  }

  /**
   * Evaluates the value of pieces that can be captured.
   * Returns higher values for more valuable pieces.
   */
  public static getCaptureValue(move: Position, boardState: BoardState): number {
    const capturedPiece = boardState.pieces.find(p => 
      p.position.row === move.row && p.position.col === move.col
    );

    if (!capturedPiece) return 0;

    // Piece values: queen > rook > bishop/knight > pawn
    switch (capturedPiece.type) {
      case 'queen': return 9;
      case 'rook': return 5;
      case 'bishop': 
      case 'knight': return 3;
      case 'pawn': return 1;
      case 'king': return 100; // Extremely high value for king capture
      default: return 0;
    }
  }

  /**
   * Prioritizes moves, placing capture moves at the front of the array sorted by piece value.
   */
  public static prioritizeCaptureMoves(moves: Position[], boardState: BoardState): Position[] {
    return [...moves].sort((a, b) => {
      const valueA = this.getCaptureValue(a, boardState);
      const valueB = this.getCaptureValue(b, boardState);
      return valueB - valueA; // Sort in descending order of value
    });
  }
}
